import { lazy } from "react";
import { reportToDatadog } from "./reportToDatadog";
export const FORCE_RELOAD_SESSION_KEY = "force_reload_at";
export const FORCE_RELOAD_INTERVAL_MS = 10000;
/**
 * Try to extract the chunk URL from the dynamic import error message.
 * Vite typically produces messages like:
 *   "Failed to fetch dynamically imported module: https://…/Foo-AbCd1234.js"
 */
function extractChunkUrl(error) {
    const msg = error instanceof Error ? error.message : String(error);
    const match = msg.match(/https?:\/\/\S+\.js\b/);
    return match ? match[0] : null;
}
/**
 * Probe the chunk URL to gather diagnostic info for error reporting.
 * Returns a short summary string (never throws).
 */
async function probeChunk(url) {
    var _a, _b;
    try {
        const res = await fetch(url, { method: "HEAD", cache: "no-store" });
        const ct = (_a = res.headers.get("content-type")) !== null && _a !== void 0 ? _a : "unknown";
        const cfRay = (_b = res.headers.get("cf-ray")) !== null && _b !== void 0 ? _b : "none";
        return `probe: status=${res.status}, content-type=${ct}, cf-ray=${cfRay}`;
    }
    catch (probeError) {
        return `probe: network error (${probeError instanceof Error ? probeError.message : String(probeError)})`;
    }
}
/**
 * Wrapper around React.lazy that handles chunk load failures
 * (e.g. after a deploy replaces old assets) by reloading the page.
 *
 * Only reloads in the SPA (Vite) context where old chunks are gone after deploy.
 * In Next.js, chunk failures are re-thrown so Next.js can handle them natively.
 *
 * To avoid infinite reload loops, reuses the same "force_reload_at" sessionStorage
 * key as the SWR reload guard and skips the reload if one happened within the last
 * 60 seconds.
 */
const RETRY_DELAY_MS = 1500;
const MAX_RETRIES = 2;
/**
 * Wait for a given number of milliseconds.
 */
function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function safeLazy(factory) {
    return lazy(async () => {
        // In Next.js (detected via __NEXT_DATA__), let the framework handle errors.
        const isNextJs = typeof window !== "undefined" && "__NEXT_DATA__" in window;
        let lastError;
        // Attempt the import with retries before resorting to a full page reload.
        for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
            try {
                return await factory();
            }
            catch (error) {
                lastError = error;
                if (isNextJs) {
                    throw error;
                }
                if (attempt < MAX_RETRIES) {
                    reportToDatadog(error, {
                        source: "safeLazy",
                        event: "retry",
                        attempt: attempt + 1,
                        maxAttempts: MAX_RETRIES + 1,
                    });
                    await delay(RETRY_DELAY_MS);
                }
            }
        }
        // All retries exhausted — enrich the error with diagnostic information.
        const chunkUrl = extractChunkUrl(lastError);
        const probeInfo = chunkUrl ? await probeChunk(chunkUrl) : "no URL found";
        const online = navigator.onLine ? "online" : "offline";
        const diagnostic = `[safeLazy] ${lastError instanceof Error ? lastError.message : String(lastError)} | ${probeInfo} | navigator=${online}`;
        // Guard against continuous reload loops.
        const lastReloadMs = sessionStorage.getItem(FORCE_RELOAD_SESSION_KEY);
        const nowMs = Date.now();
        const lastMs = lastReloadMs !== null ? Number(lastReloadMs) : Number.NaN;
        const shouldReload = !Number.isFinite(lastMs) || nowMs - lastMs > FORCE_RELOAD_INTERVAL_MS;
        if (!shouldReload) {
            throw new Error(diagnostic, { cause: lastError });
        }
        // In SPA (Vite), reload to fetch fresh assets.
        reportToDatadog(lastError, {
            source: "safeLazy",
            event: "reload",
            diagnostic,
        });
        sessionStorage.setItem(FORCE_RELOAD_SESSION_KEY, nowMs.toString());
        window.location.reload();
        return new Promise(() => { });
    });
}
//# sourceMappingURL=safeLazy.js.map